import { createAction, props } from '@ngrx/store';
import {
  ICompanyBusinessUnits,
  ICustomerAddressesData,
  ICustomerAddressData,
  ICustomerAddressPayloadData,
  ICustomerPreferences,
} from '../models/customer.models';
import { IAddress, IPointOfContact } from '../models/common.models';
import { ISoldToAddressesResponse } from '../models/soldTo-selection.models';

export const CompanyUsersActionStart = createAction(
  '[Customer] - Start Get Company Users',
);

// IMPORTANT: This action serves as the point at which the user has successfully logged in
// and the company user data has been loaded from Spryker. Catching this action in an effect
// is the way how to load additional data for the customer in the background (orders history,
// homepage banner installed base from fleet)
export const CompanyUsersActionSuccess = createAction(
  '[Customer] - Success Get Company Users',
  props<{payload: any}>(),
);

export const CompanyUsersActionError = createAction(
  '[Customer] - Error Get Company Users',
  props<{error: any}>(),
);

export const updateCustomerPreferences = createAction(
  '[Customer] - Update customer preferences',
  props<{data: ICustomerPreferences}>(),
);

export const setStore = createAction(
  '[Customer] - Set store',
  props<{store: any}>(),
);

export const loadCustomerData = createAction(
  '[Customer] - Load role and cart',
);

export const loadCustomerDataError = createAction(
  '[Customer] - Error Load role and cart',
  props<{error: any}>(),
);

export const setConsentedTerms = createAction(
  '[Customer] - Set consented terms',
  props<{newConsentedTerms: string[]}>(),
);

export const startGetBusinessUnitAction = createAction(
  '[Customer] - Begin Get Business Unit',
);

export const successGetBusinessUnitAction = createAction(
  '[Customer] - Success Get Business Unit',
  props<{payload: ICompanyBusinessUnits}>(),
);

export const errorGetBusinessUnitAction = createAction(
  '[Customer] - Error Get Business Unit',
  props<{error: any}>(),
);

export const beginGetCustomerAddressAction = createAction(
  '[Customer] - Begin Get Customer Address',
  props<{payload: string}>(),
);

export const successGetCustomerAddressAction = createAction(
  '[Customer] - Success Get Customer Address',
  props<{payload: ICustomerAddressesData}>(),
);

export const errorGetCustomerAddressAction = createAction(
  '[Customer] - Error Get Customer Address',
  props<{error: any}>(),
);

export const beginAddCustomerAddressAction = createAction(
  '[Customer] - Begin Add Customer Address',
  props<{customerId: string, data: ICustomerAddressPayloadData}>(),
);

export const successAddCustomerAddressAction = createAction(
  '[Customer] - Success Add Customer Address',
  props<{data: ICustomerAddressData}>(),
);

export const errorAddCustomerAddressAction = createAction(
  '[Customer] - Error Add Customer Address',
  props<{error: any}>(),
);

export const beginUpdateCustomerAddressAction = createAction(
  '[Customer] - Begin Update Customer Address',
  props<{customerId: string, addressId: string, data: ICustomerAddressPayloadData}>(),
);

export const successUpdateCustomerAddressAction = createAction(
  '[Customer] - Success Update Customer Address',
  props<{data: ICustomerAddressData}>(),
);

export const errorUpdateCustomerAddressAction = createAction(
  '[Customer] - Error Update Customer Address',
  props<{error: any}>(),
);

export const beginDeleteCustomerAddressAction = createAction(
  '[Customer] - Begin Delete Customer Address',
  props<{customerId: string, addressId: string, isPreferredShipToAddress: boolean}>(),
);

export const successDeleteCustomerAddressAction = createAction(
  '[Customer] - Success Delete Customer Address',
  props<{customerId: string, addressId: string}>(),
);

export const errorDeleteCustomerAddressAction = createAction(
  '[Customer] - Error Delete Customer Address',
  props<{error: any}>(),
);

export const loadCustomerShipToAddresses = createAction(
  '[Customer] - Load customer ShipTo addresses',
);

export const loadCustomerShipToAddressesSuccess = createAction(
  '[Customer] - Success Load customer ShipTo addresses',
  props<{payload: any}>(),
);

export const loadCustomerShipToAddressesError = createAction(
  '[Customer] - Error Load customer ShipTo addresses',
  props<{error: any}>(),
);

export const loadCustomerSoldToAddresses = createAction(
  '[Customer] - Load customer SoldTo addresses',
);

export const loadCustomerSoldToAddressesSuccess = createAction(
  '[Customer] - Success Load customer SoldTo addresses',
  props<{payload: ISoldToAddressesResponse}>(),
);

export const loadCustomerSoldToAddressesError = createAction(
  '[Customer] - Error Load customer SoldTo addresses',
  props<{error: any}>(),
);

export const setPointOfContact = createAction(
  `[Customer] Set Point Of Contact`,
  props<{data: IPointOfContact}>(),
);

export const clearAttentionTo = createAction(
  `[Customer] Clear Attention To`,
);

export const beginDeletedPreferredShipToAddressRemovalAction = createAction(
  '[Customer] - Begin Deleted Preferred ShipTo Address Removal',
  props<{customerId: string, addressId: string,}>(),
);

export const successDeletedPreferredShipToAddressRemovalAction = createAction(
  '[Customer] - Success Deleted Preferred ShipTo Address Removal',
  props<{customerId: string, addressId: string, data: any}>(),
);

export const errorDeletedPreferredShipToAddressRemovalAction = createAction(
  '[Customer] - Error Deleted Preferred ShipTo Address Removal',
  props<{error: any}>(),
);

/**
 * @Description Used for clearing a Point Of Contact on the 2. delivery page when switching or deleting the current cart.
 */
export const clearPointOfContact = createAction(
  `[Customer] Clear Point Of Contact`,
);

export const setCustomShipToAddress = createAction(
  `[Customer] Set Custom Ship To Address`,
  props<{data: IAddress}>(),
);

/**
 * @Description Used for clearing a customer's custom ship address on the 1. delivery page when switching or deleting
 * the current cart.
 */
export const clearCustomShipToAddress = createAction(
  `[Customer] Clear Custom Ship To Address`,
);

/**
 * @Description Used for clearing a customer's roles from localstorage and state
 * (clearing just from state because ngrx-store-localstorage/localStorageSync will sync localstorage).
 * @type {ActionCreator<'[Customer] - Clear Company Roles', () => TypedAction<'[Customer] - Clear Company Roles'>>}
 */
export const clearCompanyRoles = createAction(
  '[Customer] - Clear Company Roles',
);
